import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import { BodyText, MaterialSymbol, UtilButton } from 'UI';
import { IntegrationWorkflow, IntegrationWorkflowId, IntegrationWorkflowsHowItWorks } from '../types';
import IntegrationInstructionsDialog from './IntegrationInstructionsDialog';
import IntegrationSteps from './IntegrationSteps';
import styles from './IntegrationWorkflowsDialog.module.scss';

const cn = withStyles(styles);

type IntegrationWorkflowsDialogProps = {
  howItWorks: IntegrationWorkflowsHowItWorks;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * "How it works" for an integration with more than one workflow (Plaid): a chooser first, then the
 * chosen workflow's steps with a link across to the other one. Closing returns to the chooser.
 */
export default function IntegrationWorkflowsDialog({ howItWorks, isOpen, onClose }: IntegrationWorkflowsDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Integrations.IntegrationWorkflowsDialog' });
  // Catalogue copy resolves against a root t with full keys (see ../types.ts)
  const { t: tRoot } = useTranslation();
  const { intro, workflows } = howItWorks;
  const [selectedId, setSelectedId] = useState<IntegrationWorkflowId | null>(null);

  const selected = workflows.find((workflow) => workflow.id === selectedId);

  function handleClose() {
    setSelectedId(null);
    onClose();
  }

  function renderChooser() {
    return (
      <div className={cn('chooser')}>
        <BodyText weight="md">{intro(tRoot)}</BodyText>

        <div className={cn('options')}>
          {workflows.map((workflow) => (
            <button
              key={workflow.id}
              type="button"
              className={cn('option')}
              data-testid={`integration-workflow-${workflow.id}`}
              onClick={() => setSelectedId(workflow.id)}
            >
              <MaterialSymbol icon="device_hub" size="50px" className={cn('optionIcon')} />

              <span className={cn('optionLabel')}>
                {t('workflowLabel', { defaultValue: 'Workflow {{number}}', number: workflow.number })}
              </span>

              <span className={cn('optionName')}>{workflow.name(tRoot)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  function renderWorkflow(workflow: IntegrationWorkflow) {
    const others = workflows.filter((other) => other.id !== workflow.id);

    return (
      <div className={cn('workflow')}>
        {workflow.intro && <BodyText weight="md">{workflow.intro(tRoot)}</BodyText>}
        <IntegrationSteps steps={workflow.steps} />

        {others.map((other) => (
          <UtilButton
            key={other.id}
            type="button"
            iconRight="help"
            className={cn('seeWorkflow')}
            onClick={() => setSelectedId(other.id)}
          >
            {t('seeWorkflow', { defaultValue: 'See Workflow {{number}}', number: other.number })}
          </UtilButton>
        ))}
      </div>
    );
  }

  return (
    <IntegrationInstructionsDialog
      icon={selected ? 'device_hub' : 'help'}
      title={
        selected ? (
          <>
            <span className={cn('workflowPrefix')}>
              {t('workflowTitle', { defaultValue: 'Workflow {{number}}:', number: selected.number })}
            </span>{' '}
            {selected.name(tRoot)}
          </>
        ) : (
          t('title', { defaultValue: 'How it works' })
        )
      }
      isOpen={isOpen}
      onClose={handleClose}
    >
      {selected ? renderWorkflow(selected) : renderChooser()}
    </IntegrationInstructionsDialog>
  );
}
